import { GameState, BuildingState } from '../core/GameState';
import { bn, Decimal } from '../core/BigNumber';
import { eventBus } from '../core/EventBus';
import { BUILDINGS, BuildingDef } from '../data/buildings';
import { buildingPrice, buildingBulkPrice } from '../utils/math';

export class BuildingSystem {
  init(state: GameState): void {
    for (const def of BUILDINGS) {
      if (!state.buildings.has(def.id)) {
        state.buildings.set(def.id, this.createDefault());
      }
    }
  }

  buy(state: GameState, buildingId: string, count: number = 1): number {
    const def = this.getDef(buildingId);
    if (!def) return 0;
    const bs = state.buildings.get(buildingId);
    if (!bs) return 0;

    let bought = 0;
    for (let i = 0; i < count; i++) {
      const price = this.getPrice(def, bs);
      if (state.cookies.lt(price)) break;
      state.cookies = state.cookies.sub(price);
      bs.amount++;
      bs.bought++;
      bought++;
    }

    if (bought > 0) {
      eventBus.emit('building:bought', buildingId, bought);
      eventBus.emit('gains:recalculate');
    }
    return bought;
  }

  sell(state: GameState, buildingId: string, count: number = 1): number {
    const def = this.getDef(buildingId);
    if (!def) return 0;
    const bs = state.buildings.get(buildingId);
    if (!bs || bs.amount <= 0) return 0;

    let sold = 0;
    for (let i = 0; i < count; i++) {
      if (bs.amount <= 0) break;
      bs.amount--;
      const refund = this.getPrice(def, bs).mul(0.25).ceil();
      state.cookies = state.cookies.add(refund);
      sold++;
    }

    if (sold > 0) {
      eventBus.emit('building:sold', buildingId, sold);
      eventBus.emit('gains:recalculate');
    }
    return sold;
  }

  getPrice(def: BuildingDef, bs: BuildingState): Decimal {
    return buildingPrice(bn(def.basePrice), def.priceMultiplier, bs.amount);
  }

  getBulkPrice(def: BuildingDef, bs: BuildingState, count: number): Decimal {
    return buildingBulkPrice(bn(def.basePrice), def.priceMultiplier, bs.amount, count);
  }

  getTotalOwned(state: GameState): number {
    let total = 0;
    state.buildings.forEach((bs) => { total += bs.amount; });
    return total;
  }

  getDef(id: string): BuildingDef | undefined {
    return BUILDINGS.find((b) => b.id === id);
  }

  private createDefault(): BuildingState {
    return {
      amount: 0,
      bought: 0,
      totalCookies: bn(0),
      level: 0,
      free: 0,
      minigameSave: '',
    };
  }
}
